import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * The URL key the chips are kept under. In the URL, not in component state
 * alone, because a list page unmounts whenever it navigates away — to an edit
 * page, say — and state alone came back empty: every filter gone after
 * opening one row and pressing Cancel. The URL survives that, a refresh, and
 * a shared link. Only "Clear all" (or removing a chip) takes one away.
 */
const URL_KEY = "filters";

/**
 * A chip's value when it asks for rows where the field is EMPTY ("Genre is
 * blank"). A token, not `""` or `null`: those already mean "no filter" — this
 * hook drops them and every endpoint skips them — so sending one would match
 * everything. The backend's `helpers/blankFilter.js` reads the same string.
 * It is an ordinary non-empty value here, so it is sent, kept in the URL and
 * restored like any other.
 */
export const BLANK_FILTER_VALUE = "__blank__";

/** `{ c: [[field, value], ...], m: matchMode }` from the URL, or null. */
const readFromUrl = (searchParams) => {
  const raw = searchParams.get(URL_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const pairs = Array.isArray(parsed?.c)
      ? parsed.c.filter((p) => Array.isArray(p) && typeof p[0] === "string")
      : [];
    return { pairs, matchMode: parsed?.m === "all" || parsed?.m === "any" ? parsed.m : null };
  } catch {
    // A hand-edited or truncated link should open the list, not crash it.
    return null;
  }
};

const hasValue = (value) =>
  Array.isArray(value) ? value.some((v) => String(v).trim()) : Boolean(String(value ?? "").trim());

/**
 * The state behind <FilterBar />: a list of field/value pairs, plus whether
 * they combine with AND or OR.
 *
 * Every Data list endpoint already accepts its filter fields as separate named
 * keys on `filters` and ANDs them into one `$match` — `getAllBooks`,
 * `getAllFavoriteAuthors`, `getAllSummary`, `getAllReference`,
 * `getAllCollections` all do. So "Match all" needs nothing from the backend;
 * the client simply never sent more than one field at a time.
 *
 * `matchMode: "any"` DOES need the backend, which is why `apiFilters` sends it
 * explicitly rather than the client pretending. See `matchMode` in
 * adminService.
 */
export const useFilterChips = ({ supportsMatchAny = false, stickyField = null } = {}) => {
  // The one filter a list is normally searched by — its name field — present
  // from the first render and not removable. With the search box gone, that
  // chip IS the search box, and a page that opened with no way to type a name
  // until you found it in a menu reads as a page with no search.
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultMode = supportsMatchAny ? "any" : "all";

  const [chips, setChips] = useState(() => {
    const sticky = stickyField
      ? [{ id: `sticky-${stickyField}`, field: stickyField, value: "", sticky: true }]
      : [];
    const saved = readFromUrl(searchParams);
    if (!saved) return sticky;

    // The sticky field's saved value goes back into its own chip; everything
    // else becomes the chips it was.
    const restored = [...sticky];
    saved.pairs.forEach(([field, value], index) => {
      if (stickyField && field === stickyField && restored[0]?.value === "") {
        restored[0] = { ...restored[0], value };
      } else {
        restored.push({ id: `${field}-restored-${index}`, field, value });
      }
    });
    return restored;
  });
  // "any" wherever the toggle is real. A page whose endpoint only ever ANDs
  // stays on "all": sending a mode the server ignores would make the stored
  // state disagree with the rows on screen, with no control to show for it.
  const [matchMode, setMatchMode] = useState(() => {
    const saved = readFromUrl(searchParams)?.matchMode;
    // A saved "any" on a page that cannot honour it falls back to "all".
    return saved && (supportsMatchAny || saved === "all") ? saved : defaultMode;
  });

  // Mirror chips that carry a value into the URL. `replace`, so typing into a
  // chip does not stack a history entry per keystroke; merged into the
  // router's current query, so other keys a page keeps there (the page number)
  // are untouched. The router's params, not `window.location` — the two only
  // agree under a browser router, and comparing against the wrong one let
  // "Clear all" leave the old filters in the URL.
  useEffect(() => {
    const pairs = chips.filter((c) => hasValue(c.value)).map((c) => [c.field, c.value]);
    const next = new URLSearchParams(searchParams);

    if (pairs.length === 0 && matchMode === defaultMode) {
      next.delete(URL_KEY);
    } else {
      next.set(URL_KEY, JSON.stringify({ c: pairs, m: matchMode }));
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [chips, matchMode, defaultMode, searchParams, setSearchParams]);

  const addChip = useCallback((field) => {
    // `id` rather than the field name as the key: the same field can be added
    // twice (two genres, say), and a keyed-by-field list would collapse them.
    setChips((prev) => [
      ...prev,
      { id: `${field}-${Date.now()}-${prev.length}`, field, value: "" },
    ]);
  }, []);

  const updateChip = useCallback((id, value) => {
    setChips((prev) => prev.map((c) => (c.id === id ? { ...c, value } : c)));
  }, []);

  const removeChip = useCallback((id) => {
    // A sticky chip is emptied, never removed — there would be no way to get
    // it back except through the Add filter menu, where it is not offered
    // while it is in use.
    setChips((prev) =>
      prev.flatMap((c) =>
        c.id !== id ? [c] : c.sticky ? [{ ...c, value: "" }] : []
      )
    );
  }, []);

  const clearChips = useCallback(
    () => setChips((prev) => prev.filter((c) => c.sticky).map((c) => ({ ...c, value: "" }))),
    []
  );

  /** The chips folded into the `filters` shape the endpoints already read. */
  const chipFilters = useMemo(() => {
    const out = {};
    chips.forEach(({ field, value }) => {
      // Genre and profession hold a LIST — several picked values, which the
      // endpoints read as "any of these". Sent as an array rather than a
      // joined string so a name containing a comma cannot split in two.
      if (Array.isArray(value)) {
        const picked = value.map((v) => String(v).trim()).filter(Boolean);
        if (picked.length) out[field] = picked;
        return;
      }

      const trimmed = (value || "").trim();
      if (!trimmed) return;
      // Last one wins. Joining two values with a comma was worse than
      // useless: every endpoint feeds the value straight into `$regex`, so
      // "Janet,Fiction" is a literal that matches nothing and the list silently
      // empties. <FilterBar> also stops a field being added twice, so this is
      // only the backstop.
      out[field] = trimmed;
    });
    return out;
  }, [chips]);

  /**
   * A primitive the list pages can put in a dependency array and in their
   * fetch guard. Debounced, because a chip is typed into: without this every
   * keystroke is a request, and the pages already debounce their search box
   * by 1000ms for the same reason.
   */
  const rawSignature = useMemo(
    () => JSON.stringify({ chipFilters, matchMode }),
    [chipFilters, matchMode]
  );
  const [signature, setSignature] = useState(rawSignature);
  useEffect(() => {
    const timer = setTimeout(() => setSignature(rawSignature), 600);
    return () => clearTimeout(timer);
  }, [rawSignature]);

  return {
    chips,
    matchMode,
    setMatchMode,
    addChip,
    updateChip,
    removeChip,
    clearChips,
    chipFilters,
    signature,
  };
};
